import React from "react";
import { Check, ArrowRight } from "lucide-react";

/**
 * Visual stepper for the lead lifecycle.
 * States: "À contacter" → "Contacté" → "En discussion" → "RDV planifié" → "Converti" (or "Perdu")
 *
 * Props:
 *  - status: current status string (matches backend)
 *  - onSelect: (newStatus) => void (optional — makes steps clickable)
 *  - compact: smaller variant (default false)
 */
const STEPS = [
  { id: "À contacter", label: "Nouveaux leads" },
  { id: "Contacté", label: "Contacté" },
  { id: "En discussion", label: "En discussion" },
  { id: "RDV planifié", label: "RDV planifié" },
  { id: "Converti", label: "Converti" },
];

export default function ProspectStepper({ status, onSelect, compact = false, testId }) {
  const current = STEPS.findIndex((s) => s.id === status);
  const isLost = status === "Perdu";
  const active = Math.max(0, current);

  return (
    <div data-testid={testId || "prospect-stepper"} className="w-full">
      <div className="flex items-center gap-1">
        {STEPS.map((s, i) => {
          const done = i < active;
          const curr = i === active && !isLost;
          const clickable = !!onSelect;
          return (
            <React.Fragment key={s.id}>
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onSelect?.(s.id)}
                data-testid={`step-${s.id}`}
                className={`flex flex-col items-center flex-1 min-w-0 ${clickable ? "cursor-pointer" : ""}`}
              >
                <div
                  className={`${compact ? "w-6 h-6 text-[10px]" : "w-8 h-8 text-xs"} rounded-full grid place-items-center font-bold transition-all shrink-0 ${
                    done
                      ? "bg-emerald-500/100 text-white"
                      : curr
                      ? "bg-gradient-to-br from-emerald-500 to-orange-500 text-white shadow-[0_0_12px_rgba(0,220,130,0.5)]"
                      : isLost
                      ? "bg-rose-500/20 text-rose-300 border border-rose-400/30"
                      : "bg-white/[0.06] border border-white/10 text-zinc-500"
                  }`}
                >
                  {done ? <Check className={compact ? "w-3 h-3" : "w-4 h-4"} /> : i + 1}
                </div>
                <div
                  className={`${compact ? "text-[9px]" : "text-[10px]"} mt-1 font-semibold text-center leading-tight ${
                    curr ? "text-white" : done ? "text-emerald-300" : "text-zinc-500"
                  }`}
                >
                  {s.label}
                </div>
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className={`${compact ? "h-0.5 mt-[-14px]" : "h-0.5 mt-[-20px]"} flex-1 rounded-full ${
                    i < active ? "bg-emerald-500/100" : "bg-white/10"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {isLost && (
        <div className="mt-2 text-center text-[11px] font-bold text-rose-300">Lead perdu</div>
      )}
    </div>
  );
}

ProspectStepper.STEPS = STEPS;
